import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiChevronDown, FiMail, FiSearch, FiSend } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAccountOfficerAssignedCustomersWithRisk } from "../../redux/thunk";
import type { RootState } from "../../redux/store";
import { Preloader } from "../../components/elements/Preloader";
import type { AccountOfficerAssignedCustomerWithRisk } from "../../types/types";

const MAX_MESSAGE_LENGTH = 500;
const USERS_PAGE_SIZE = 1000;

function formatCustomerName(c: AccountOfficerAssignedCustomerWithRisk) {
  const name = `${c.firstName || ""} ${c.lastName || ""}`.trim();
  return name || c.email || "Unknown";
}

export default function PushNotification() {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((s: RootState) => s.auth);
  const {
    assignedCustomers,
    assignedCustomersStatus,
    assignedCustomersError,
  } = useAppSelector((s: RootState) => s.accountOfficer);

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isSending, setIsSending] = useState(false);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const audienceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(
      getAccountOfficerAssignedCustomersWithRisk({
        page: 1,
        limit: USERS_PAGE_SIZE,
        search: "",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!isAudienceOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!audienceRef.current) return;
      if (audienceRef.current.contains(e.target as Node)) return;
      setIsAudienceOpen(false);
    };
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [isAudienceOpen]);

  const filteredCustomers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return assignedCustomers;
    return assignedCustomers.filter((c) => {
      const name = formatCustomerName(c).toLowerCase();
      const email = String(c.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [assignedCustomers, userSearch]);

  const selectedCustomers = useMemo(() => {
    if (selectedCustomerIds.size === 0) return [];
    const selected = new Set(selectedCustomerIds);
    return assignedCustomers.filter((c) => selected.has(c.customerId));
  }, [assignedCustomers, selectedCustomerIds]);

  const areAllFilteredSelected = useMemo(() => {
    if (filteredCustomers.length === 0) return false;
    for (const c of filteredCustomers) {
      if (!selectedCustomerIds.has(c.customerId)) return false;
    }
    return true;
  }, [filteredCustomers, selectedCustomerIds]);

  const toggleCustomer = (customerId: string) => {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedCustomerIds((prev) => {
      const next = new Set(prev);
      const shouldSelectAll = !areAllFilteredSelected;
      for (const c of filteredCustomers) {
        if (shouldSelectAll) next.add(c.customerId);
        else next.delete(c.customerId);
      }
      return next;
    });
  };

  const validate = () => {
    if (!title.trim()) {
      toast.error("Please enter notification title");
      return false;
    }
    if (!message.trim()) {
      toast.error("Please enter a message");
      return false;
    }
    if (selectedCustomerIds.size === 0) {
      toast.error("Please select at least one user");
      return false;
    }
    return true;
  };

  const send = async () => {
    if (!validate()) return;
    if (!token) {
      toast.error("Authentication token not found");
      return;
    }

    const emails = Array.from(
      new Set(
        selectedCustomers
          .map((r) => String(r.email || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    );

    if (emails.length === 0) {
      toast.error("No assigned users found");
      return;
    }

    setIsSending(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/sendPushNotificationToMultipleEmails`,
        {
          emails,
          title: title.trim(),
          body: message.trim(),
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      const sent = Number(res.data?.sent ?? 0);
      const failed = Number(res.data?.failed ?? 0);

      if (sent > 0 && failed === 0) {
        toast.success(res.data?.message || "Notification sent successfully");
      } else if (sent > 0 && failed > 0) {
        toast.success("Notification sent successfully");
        toast.error("Some recipients could not be reached");
      } else {
        toast.error(res.data?.message || "Failed to send notification");
      }
      setTitle("");
      setMessage("");
      setSelectedCustomerIds(new Set());
      setUserSearch("");
      setIsAudienceOpen(false);
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Push Notifications
        </h1>
        <p className="text-sm text-gray-500">
          Send targeted notifications to your assigned users
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <section className="rounded-2xl border border-gray-100 bg-white p-6">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Create Notification
              </h2>
              <p className="text-sm text-gray-500">
                Compose and send push notifications
              </p>
            </div>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 text-[#008751]">
              <FiSend className="h-4 w-4" />
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Notification Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter notification title"
                className="h-12 w-full rounded-xl border border-gray-200 px-4 text-sm outline-none transition focus:border-[#008751]"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter message here..."
                maxLength={MAX_MESSAGE_LENGTH}
                className="min-h-[96px] w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#008751]"
              />
              <div className="mt-1 text-xs text-gray-400">
                {message.length}/{MAX_MESSAGE_LENGTH} Characters
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Target Audience
              </label>
              <div className="relative" ref={audienceRef}>
                <button
                  type="button"
                  onClick={() => setIsAudienceOpen((v) => !v)}
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 pr-10 text-left text-sm text-gray-500 outline-none transition hover:bg-gray-50 focus:border-[#008751]"
                >
                  Select Users
                </button>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#008751]">
                  <FiChevronDown className="h-4 w-4" />
                </span>

                {isAudienceOpen && (
                  <div className="absolute z-20 mt-2 w-full rounded-2xl border border-gray-100 bg-white p-3 shadow-lg">
                    <div className="mb-3">
                      <div className="relative">
                        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          placeholder="Search User"
                          className="h-11 w-full rounded-xl border border-gray-200 pl-10 pr-3 text-sm outline-none transition focus:border-[#008751]"
                        />
                      </div>
                    </div>

                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-700">
                        Assigned users
                      </div>
                      <label className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Select all</span>
                        <input
                          type="checkbox"
                          checked={areAllFilteredSelected}
                          onChange={toggleSelectAllFiltered}
                          className="h-4 w-4 accent-[#008751]"
                        />
                      </label>
                    </div>

                    <div className="max-h-56 overflow-y-auto pr-1">
                      {assignedCustomersStatus === "loading" ? (
                        <div className="py-8 text-center text-sm text-gray-500">
                          Loading users...
                        </div>
                      ) : assignedCustomersStatus === "failed" ? (
                        <div className="py-8 text-center text-sm text-gray-500">
                          {assignedCustomersError || "Failed to load users"}
                        </div>
                      ) : filteredCustomers.length === 0 ? (
                        <div className="py-8 text-center text-sm text-gray-500">
                          No users found
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {filteredCustomers.map((c) => {
                            const checked = selectedCustomerIds.has(
                              c.customerId,
                            );
                            return (
                              <li key={c.customerId}>
                                <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-gray-50">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCustomer(c.customerId)}
                                    className="h-4 w-4 accent-[#008751]"
                                  />
                                  <div className="min-w-0">
                                    <div className="truncate text-xs font-medium text-gray-900">
                                      {formatCustomerName(c)}
                                    </div>
                                    <div className="truncate text-[11px] text-gray-400">
                                      {c.email}
                                    </div>
                                  </div>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[#008751]">
                  <FiMail className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-[#008751]">
                    {selectedCustomers.length > 0
                      ? selectedCustomers.map((c) => c.email).join(" , ")
                      : "No users selected"}
                  </div>
                  <div className="text-xs text-[#008751]">Selected:</div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={send}
              disabled={isSending}
              className="h-12 w-full rounded-xl bg-[#008751] text-sm font-semibold text-white transition hover:bg-[#007a49] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSending ? <Preloader /> : "Send Notification"}
            </button>
          </div>
        </section>

        <aside className="rounded-2xl border border-gray-100 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                All Assigned Users
              </h3>
              <p className="text-xs text-gray-400">All Assigned Users</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-500">
              <span>Select all</span>
              <input
                type="checkbox"
                checked={areAllFilteredSelected}
                onChange={toggleSelectAllFiltered}
                className="h-4 w-4 accent-[#008751]"
              />
            </label>
          </div>

          <div className="mb-4">
            <div className="relative">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search User"
                className="h-11 w-full rounded-xl border border-gray-200 pl-10 pr-3 text-sm outline-none transition focus:border-[#008751]"
              />
            </div>
          </div>

          <div className="max-h-[440px] overflow-y-auto pr-1">
            {assignedCustomersStatus === "loading" ? (
              <div className="py-10 text-center text-sm text-gray-500">
                Loading users...
              </div>
            ) : assignedCustomersStatus === "failed" ? (
              <div className="py-10 text-center text-sm text-gray-500">
                {assignedCustomersError || "Failed to load users"}
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">
                No users found
              </div>
            ) : (
              <ul className="space-y-1">
                {filteredCustomers.map((c) => {
                  const checked = selectedCustomerIds.has(c.customerId);
                  return (
                    <li key={c.customerId}>
                      <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCustomer(c.customerId)}
                          className="h-4 w-4 accent-[#008751]"
                        />
                        <div className="min-w-0">
                          <div className="truncate text-xs font-medium text-gray-900">
                            {formatCustomerName(c)}
                          </div>
                          <div className="truncate text-[11px] text-gray-400">
                            {c.email}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

